import { forwardRef, useEffect, useRef, useState } from 'react'
import { BetsCycleLoader } from '@/components/ui/betscycle-loader'
import { SlideBetPanel } from '@/components/original/games/ui/SlideBetPanel'
import { AssetManager } from '@/components/original/games/slide/core/AssetManager'
import { GameController } from '@/components/original/games/slide/core/GameController'
import { SoundManager } from '@/components/original/games/slide/core/SoundManager'
import { RoundHistoryBar } from '@/components/original/games/slide/components/RoundHistoryBar'
import { RoundStatusBar } from '@/components/original/games/slide/components/RoundStatusBar'
import { SlideTrack } from '@/components/original/games/slide/components/SlideTrack'
import { disconnectSlideSocket } from '@/store/socket/slide-socket'
import { cn } from '@/lib/utils'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { resetSlideGame, selectSlideGame, setSlideCurrencyCode } from '@/store/slices/slideSlice'

interface SlideGameProps {
	currencyCode: string
	className?: string
}

export const SlideGame = forwardRef<HTMLDivElement, SlideGameProps>(function SlideGame({ currencyCode, className }, ref) {
	const dispatch = useAppDispatch()
	const slide = useAppSelector(selectSlideGame)
	const controllerRef = useRef<GameController | null>(null)
	const [controller, setController] = useState<GameController | null>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [loadError, setLoadError] = useState(false)
	const [loadProgress, setLoadProgress] = useState(0)

	const setRootRef = (node: HTMLDivElement | null) => {
		if (typeof ref === 'function') {
			ref(node)
		} else if (ref) {
			ref.current = node
		}
	}

	useEffect(() => {
		dispatch(setSlideCurrencyCode(currencyCode))
	}, [currencyCode, dispatch])

	useEffect(() => {
		let cancelled = false

		const boot = async () => {
			setIsLoading(true)
			setLoadError(false)

			try {
				await AssetManager.loadAll((progress) => {
					if (!cancelled) {
						setLoadProgress(Math.round(progress * 100))
					}
				})
				await SoundManager.instance.load()

				if (cancelled) {
					return
				}

				const gameController = new GameController()
				controllerRef.current = gameController
				setController(gameController)
				setIsLoading(false)
			} catch {
				if (!cancelled) {
					setLoadError(true)
					setIsLoading(false)
				}
			}
		}

		void boot()

		return () => {
			cancelled = true
			controllerRef.current?.dispose()
			controllerRef.current = null
			setController(null)
			disconnectSlideSocket()
			SoundManager.instance.stopAllGameplay()
			dispatch(resetSlideGame())
		}
	}, [dispatch])

	return (
		<div
			ref={setRootRef}
			className={cn(
				'flex w-full min-w-0 flex-col gap-4 xl:flex-row',
				'[&:fullscreen]:fixed [&:fullscreen]:inset-0 [&:fullscreen]:z-[9999] [&:fullscreen]:flex [&:fullscreen]:h-screen [&:fullscreen]:w-screen [&:fullscreen]:flex-col [&:fullscreen]:gap-4 [&:fullscreen]:bg-background [&:fullscreen]:p-4 xl:[&:fullscreen]:flex-row',
				className,
			)}
		>
			<div className='order-2 h-[420px] w-full shrink-0 overflow-hidden sm:h-[520px] xl:order-1 xl:h-[520px] xl:w-[340px] 2xl:w-[360px] [:fullscreen_&]:order-2 [:fullscreen_&]:h-auto [:fullscreen_&]:max-h-[42%] [:fullscreen_&]:w-full [:fullscreen_&]:shrink-0 xl:[:fullscreen_&]:order-1 xl:[:fullscreen_&]:h-full xl:[:fullscreen_&]:max-h-none xl:[:fullscreen_&]:w-[340px]'>
				<SlideBetPanel controller={controller} className='h-full min-h-0 [:fullscreen_&]:h-full' />
			</div>

			<div
				className={cn(
					'relative order-1 w-full min-w-0 overflow-hidden rounded-2xl border border-border bg-card',
					'aspect-[6/5]',
					'xl:order-2 xl:aspect-auto xl:min-h-[520px] xl:flex-1',
					'[:fullscreen_&]:order-1 [:fullscreen_&]:aspect-auto [:fullscreen_&]:h-auto [:fullscreen_&]:min-h-0 [:fullscreen_&]:flex-1 xl:[:fullscreen_&]:order-2 xl:[:fullscreen_&]:h-full',
				)}
			>
				<div className='absolute inset-0 bg-background'>
					<RoundHistoryBar history={slide.history} />

					<div className='relative flex h-full w-full items-center'>
						<SlideTrack slidePoint={slide.slidePoint} stripMultipliers={slide.stripMultipliers} spinning={slide.phase === 'playing'} land={slide.phase === 'result'} />

						<div className='absolute bottom-5 left-5 z-20'>
							<div className='flex items-center gap-2 rounded-full border border-border bg-card/80 px-3 py-1.5 backdrop-blur'>
								<span className='h-2 w-2 rounded-full bg-success' />
								<span className='text-xs font-medium text-foreground'>Bets: {slide.playerCount}</span>
							</div>
						</div>

						<div className='pointer-events-none absolute inset-x-0 bottom-0 z-20'>
							<RoundStatusBar
								phase={slide.phase}
								bettingEndsAt={slide.bettingEndsAt}
								bettingWindowSeconds={slide.bettingWindowSeconds}
								animationEnded={slide.animationEnded}
							/>
						</div>

						<div className='pointer-events-none absolute inset-0 z-10 bg-gradient-to-r from-background via-transparent to-background' />
					</div>
				</div>

				{isLoading ? (
					<div className='absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-card/90'>
						<BetsCycleLoader size={32} />
						<p className='text-sm font-medium text-muted-foreground'>Loading slide assets… {loadProgress}%</p>
					</div>
				) : null}

				{loadError ? (
					<div className='absolute inset-0 z-30 flex items-center justify-center bg-card p-6'>
						<p className='text-sm text-muted-foreground'>Unable to load the slide game right now.</p>
					</div>
				) : null}
			</div>
		</div>
	)
})
